import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, Progress } from '@ui';
import { toastMessage } from '@ui/components/Toast';
import { useUserStore } from '@/stores/userStore';
import { useGroupStore } from '@/stores/groupStore';
import { useGetGroup, useGetGroupMemberPage } from '@hooks/generated/group';
import { useSignConsent } from '@hooks/generated/consent';
import { uploadBase64File } from '@api/@fileUpload';
import { useUpdateMe } from '@hooks/generated/me';
import { useUpdateGroup } from '@hooks/generated/group';
import { useCreateBulkRequests, useGetRequestPage, useResendRequest } from '@hooks/generated/consent-request';
import type { GroupMemberDetailResponse, ConsentRequestSummaryResponse } from '@careforu-tailody/tailody-core-api';
import { cn } from '@ui/lib/utils';
import { User, Building2, Settings2, FileSignature, Activity, LayoutTemplate, CreditCard, FileTextIcon, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

// Types
import type { TabType, OperationsSubTab, StoreInfo, GroupData } from './types';

// Tab Components
import {
    ProfileTab,
    BranchTab,
    ConsentTab,
    ActivityTab,
    ActivityTemplateTab,
    PricingPlanTab,
    TemplateModal,
    TemplateViewModal,
    TemplateDeleteModal,
    SignatureModal,
    SignedConsentDetailModal,
    CustomerDetailModal,
    SendConsentModal,
    VersionHistoryModal,
} from './components';

// Hooks
import { useConsentTemplates, useSignedConsents, useSignature, usePdfExport } from './hooks';

export default function SettingsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<TabType>(() => {
        const tabFromUrl = searchParams.get('tab');
        if (tabFromUrl === 'profile' || tabFromUrl === 'branch' || tabFromUrl === 'operations' || tabFromUrl === 'consent') {
            return tabFromUrl;
        }
        // 이전 URL 호환성 유지 (consent -> operations)
        if (tabFromUrl === 'consent') {
            return 'operations';
        }
        return 'profile';
    });
    const [operationsSubTab, setOperationsSubTab] = useState<OperationsSubTab>(() => {
        const subTabFromUrl = searchParams.get('subTab');
        if (subTabFromUrl === 'activity' || subTabFromUrl === 'activity-template' || subTabFromUrl === 'pricing') {
            return subTabFromUrl;
        }
        return 'activity';
    });
    const { error, clearError } = useUserStore();
    const selectedGroup = useGroupStore((state) => state.selectedGroup);
    const groupId = selectedGroup?.id;

    // Custom Hooks
    const templateHook = useConsentTemplates();
    const signedConsentsHook = useSignedConsents(templateHook.templates);
    const pdfExportHook = usePdfExport();

    // 서명 API Mutation
    const signConsentMutation = useSignConsent({
        options: {
            onSuccess: () => {
                toastMessage({ message: '동의서 서명이 완료되었습니다.', type: 'success' });
            },
            onError: (error: any) => {
                toastMessage({ message: error?.response?.data?.message || '동의서 서명에 실패했습니다.', type: 'error' });
            },
        },
    });

    // Branch data for storeInfo
    const { data: groupData } = useGetGroup({
        request: { groupId: groupId || 0 },
        options: { enabled: !!groupId },
    });

    // Mutations for success messages
    const updateProfileMutation = useUpdateMe({
        options: {
            onSuccess: () => clearError(),
        },
    });

    const updateBranchMutation = useUpdateGroup({
        options: {
            onSuccess: () => clearError(),
        },
    });

    const currentGroup = groupData as GroupData | null;

    // 고객 목록 조회 (동의서 발송용)
    const { data: customerListData } = useGetGroupMemberPage({
        request: {
            groupId: groupId || 0,
            pageable: { page: 0, size: 100 },
            role: "CUSTOMER"
        },
        options: {
            enabled: !!groupId,
        },
    });

    // 고객 목록을 SendConsentModal에서 사용할 형식으로 변환
    const customers = useMemo(() => {
        const content = customerListData?.content || [];
        return (content as GroupMemberDetailResponse[]).map((member) => {
            const memberAny = member as Record<string, unknown>;
            const pets = (memberAny.pets as Array<Record<string, unknown>>) || [];
            return {
                id: String(memberAny.userId || member.userId),
                name: (memberAny.name as string) || member.name || '',
                phone: (memberAny.phoneNumber as string) || member.phoneNumber || '',
                pets: pets.map((pet) => {
                    // species/breed가 객체인 경우 name 필드 추출
                    const speciesValue = pet.species;
                    const speciesName = typeof speciesValue === 'object' && speciesValue !== null
                        ? (speciesValue as Record<string, unknown>).name as string || ''
                        : (speciesValue as string) || '';
                    const breedValue = pet.breed;
                    const breedName = typeof breedValue === 'object' && breedValue !== null
                        ? (breedValue as Record<string, unknown>).name as string || ''
                        : (breedValue as string) || '';
                    return {
                        id: String(pet.petId || ''),
                        name: (pet.name as string) || '',
                        species: speciesName,
                        breed: breedName,
                    };
                }),
            };
        });
    }, [customerListData]);

    // 서명 API 호출 핸들러
    const handleSignConsent = useCallback(async (params: {
        userId: string;
        templateId: number;
        signatureDataUrl: string;
        questionAnswers?: Array<{ displayOrder: number; answer: string | number }>;
    }) => {
        if (!groupId) {
            toastMessage({ message: '그룹 정보가 없습니다.', type: 'error' });
            return;
        }

        // signatureDataUrl에서 base64 부분만 추출 (data:image/png;base64, 제거)
        const base64Data = params.signatureDataUrl.replace(/^data:image\/\w+;base64,/, '');

        await signConsentMutation.mutateAsync({
            groupId,
            userId: String(params.userId),
            templateId: params.templateId,
            consentSignConsentRequest: {
                signatureImageBase64: base64Data,
                questionAnswers: params.questionAnswers?.map((qa) => ({
                    displayOrder: qa.displayOrder,
                    answer: { value: qa.answer } as object,
                })),
            },
        });
    }, [groupId, signConsentMutation]);

    // Signature Hook (고객 데이터와 API 연결)
    const signatureHook = useSignature(
        templateHook.templates,
        customers,
        signedConsentsHook.addSignedConsent,
        handleSignConsent
    );

    // 발송 요청 이력 조회
    const { data: sendRequestsData, refetch: refetchSendRequests } = useGetRequestPage({
        request: {
            groupId: groupId || 0,
            pageable: { page: 0, size: 100 },
        },
        options: {
            enabled: !!groupId,
        },
    });

    // 발송 요청 Mutation
    const createBulkRequestsMutation = useCreateBulkRequests({
        options: {
            onSuccess: (response) => {
                refetchSendRequests();

                // bulk API 응답에서 successList, failureList 확인
                const bulkResult = response?.data?.data as Record<string, unknown> | undefined;
                const successList = (bulkResult?.successList || []) as Array<{ userId?: string; userName?: string; customerName?: string }>;
                const failureList = (bulkResult?.failureList || []) as Array<{ userId?: string; userName?: string; customerName?: string; reason?: string; error?: string }>;

                // 결과 데이터 저장 및 결과 모달 표시
                setBulkSendResult({ successList, failureList });
                setIsBulkSendResultModalOpen(true);
            },
            onError: (error: unknown) => {
                const axiosError = error as { response?: { data?: { message?: string } } };
                toastMessage({ message: axiosError?.response?.data?.message || '동의서 발송에 실패했습니다.', type: 'error' });
            },
        },
    });

    // 재전송 요청 Mutation
    const resendRequestMutation = useResendRequest({
        options: {
            onSuccess: () => {
                refetchSendRequests();
                toastMessage({ message: '동의서가 재전송되었습니다.', type: 'success' });
            },
            onError: (error: any) => {
                toastMessage({ message: error?.response?.data?.message || '동의서 재전송에 실패했습니다.', type: 'error' });
            },
        },
    });

    // URL의 tab 파라미터 변경 감지
    useEffect(() => {
        const tabFromUrl = searchParams.get('tab');
        const subTabFromUrl = searchParams.get('subTab');

        if (tabFromUrl === 'profile' || tabFromUrl === 'branch' || tabFromUrl === 'operations') {
            setActiveTab(tabFromUrl);
        } else if (tabFromUrl === 'consent') {
            // 이전 URL 호환성 유지
            setActiveTab('consent');
        }

        if (subTabFromUrl === 'activity' || subTabFromUrl === 'activity-template' || subTabFromUrl === 'pricing') {
            setOperationsSubTab(subTabFromUrl);
        }
    }, [searchParams]);

    // 탭 변경 시 URL 업데이트
    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        if (tab === 'operations') {
            setSearchParams({ tab, subTab: operationsSubTab });
        } else {
            setSearchParams({ tab });
        }
    };

    // 운영관리 서브탭 변경
    const handleOperationsSubTabChange = (subTab: OperationsSubTab) => {
        setOperationsSubTab(subTab);
        setSearchParams({ tab: 'operations', subTab });
    };

    // 매장 정보 (동의서에 자동 입력)
    const storeInfo: StoreInfo = useMemo(() => ({
        name: currentGroup?.name || '매장명',
        address: (currentGroup as any)?.location || '주소 정보 없음',
        phone: (currentGroup as any)?.contactPhone || '전화번호 정보 없음',
        businessNumber: '',
    }), [currentGroup]);


    // ========== Template Hook Destructuring ==========
    const {
        templates,
        isTemplateModalOpen,
        isViewModalOpen,
        isDeleteModalOpen,
        isVersionHistoryModalOpen,
        selectedTemplate,
        templateForView,
        templateModalMode,
        templateFormData,
        fileInputRef,
        sensors,
        versionHistory,
        // Pagination
        pageInfo,
        goToPage,
        goToNextPage,
        goToPrevPage,
        goToFirstPage,
        goToLastPage,
        openCreateModal: handleCreateTemplate,
        openEditModal: handleEditTemplate,
        openViewModal: handleViewTemplate,
        openDeleteModal: handleDeleteConfirm,
        openVersionHistoryModal: handleOpenVersionHistory,
        closeVersionHistoryModal: handleCloseVersionHistory,
        closeModals,
        toggleTemplateMandatory: handleToggleMandatory,
        duplicateTemplate: handleDuplicateTemplate,
        deleteTemplate: handleDeleteTemplate,
        saveTemplate: handleSaveTemplate,
        setActiveVersion: handleSetActiveVersion,
        handleFileUpload,
        clearFileUpload: handleClearFileUpload,
        handleDragEnd,
        addQuestion: handleAddQuestion,
        deleteQuestion: handleDeleteQuestion,
        updateQuestion: handleUpdateQuestion,
        changeQuestionType: handleQuestionTypeChange,
        addOption: handleAddOption,
        deleteOption: handleDeleteOption,
        updateOption: handleUpdateOption,
        setTemplateFormData,
        setIsTemplateModalOpen,
        setIsViewModalOpen,
        setIsDeleteModalOpen,
        setIsVersionHistoryModalOpen,
    } = templateHook;

    // ========== Signed Consents Hook Destructuring ==========
    const {
        signedConsentsList,
        customerConsentGroups,
        isSignedDetailModalOpen,
        selectedSignedConsent,
        isCustomerDetailModalOpen,
        selectedCustomerGroup,
        selectedConsentForPreview,
        searchConsentQuery,
        setSearchConsentQuery,
        openDetailModal: handleViewSignedConsent,
        closeDetailModal,
        openCustomerDetailModal,
        closeCustomerDetailModal,
        selectConsentForPreview: handleSelectConsentForPreview,
        setIsSignedDetailModalOpen,
        setIsCustomerDetailModalOpen,
    } = signedConsentsHook;

    // ========== Signature Hook Destructuring ==========
    const {
        isSignatureModalOpen,
        signatureStep,
        signatureFormData,
        signatureDataUrl,
        signatureCanvasRef,
        activeTemplates,
        customers: signatureCustomers,
        selectedPetInfo,
        questionAnswers,
        openSignatureModal,
        openSignatureModalWithTemplate,
        closeSignatureModal,
        goToNextStep,
        completeSignature: handleCompleteSignature,
        startDrawing,
        draw,
        stopDrawing,
        clearSignature,
        setSignatureFormData,
        setSignatureStep,
        setIsSignatureModalOpen,
        setSelectedPetInfo,
        setQuestionAnswers,
    } = signatureHook;

    // ========== PDF Export Hook ==========
    const { previewRef, previewConsentRef, downloadTemplatePdf, downloadConsentPdf } = pdfExportHook;

    // ========== Send Consent Modal State ==========
    const [isSendConsentModalOpen, setIsSendConsentModalOpen] = useState(false);
    const [isSendHistoryModalOpen, setIsSendHistoryModalOpen] = useState(false);

    // ========== Bulk Send Result Modal State ==========
    const [isBulkSendResultModalOpen, setIsBulkSendResultModalOpen] = useState(false);
    const [bulkSendResult, setBulkSendResult] = useState<{
        successList: Array<{ userId?: string; userName?: string; customerName?: string }>;
        failureList: Array<{ userId?: string; userName?: string; customerName?: string; reason?: string; error?: string }>;
    } | null>(null);

    // 전송 요청 타입 정의
    type SendRequestType = {
        id: string;
        templateId: string;
        templateName: string;
        templateVersion: number;
        customerId: string;
        customerName: string;
        customerPhone: string;
        sendMethod: 'sms' | 'email' | 'kakao';
        sentAt: string;
        linkToken: string;
        linkExpiresAt?: string;
        status: 'sent' | 'viewed' | 'signed' | 'expired';
        viewedAt?: string;
        signedAt?: string;
    };

    // publicUrl에서 토큰 추출 (예: "http://careforu.iptime.org:8080/sign-consent/944f4c6b8d664273af62006dda924b5d")
    const extractTokenFromUrl = (url: string | undefined): string => {
        if (!url) return '';
        // URL에서 /sign-consent/ 이후의 토큰 추출
        const match = url.match(/\/sign-consent\/([a-zA-Z0-9]+)$/);
        return match ? match[1] : '';
    };

    // API 발송 이력 데이터를 로컬 형식으로 변환
    const sendRequests = useMemo((): SendRequestType[] => {
        const content = sendRequestsData?.content || [];
        return (content as ConsentRequestSummaryResponse[]).map((request) => {
            const requestAny = request as Record<string, unknown>;
            // publicUrl에서 토큰 추출 또는 linkToken 직접 사용
            const publicUrl = (requestAny.publicUrl as string) || '';
            const linkToken = extractTokenFromUrl(publicUrl) || (requestAny.linkToken as string) || '';
            return {
                id: String(request.id),
                templateId: String(request.templateId),
                templateName: request.templateTitle || '',
                templateVersion: (requestAny.templateVersion as number) || 1,
                customerId: String(requestAny.userId || requestAny.customerId || ''),
                customerName: (requestAny.userName as string) || (requestAny.customerName as string) || '',
                customerPhone: (requestAny.userPhone as string) || (requestAny.customerPhone as string) || '',
                sendMethod: (request.sendMethod?.toLowerCase() || 'sms') as 'sms' | 'email' | 'kakao',
                sentAt: request.sentAt || '',
                linkToken,
                linkExpiresAt: request.linkExpiresAt,
                status: (request.status?.toLowerCase() || 'sent') as 'sent' | 'viewed' | 'signed' | 'expired',
                viewedAt: requestAny.viewedAt as string | undefined,
                signedAt: requestAny.signedAt as string | undefined,
            };
        });
    }, [sendRequestsData]);

    // 동의서 통계 계산 (Phase 4)
    const consentStats = useMemo(() => {
        const totalSent = sendRequests.filter(r => r.status === 'sent').length;
        const totalViewed = sendRequests.filter(r => r.status === 'viewed').length;
        const totalSigned = sendRequests.filter(r => r.status === 'signed').length;
        const totalExpired = sendRequests.filter(r => r.status === 'expired').length;
        const total = sendRequests.length;

        return {
            totalSent,
            totalViewed,
            totalSigned,
            totalExpired,
            signRate: total > 0 ? (totalSigned / total) * 100 : 0,
            viewRate: total > 0 ? ((totalViewed + totalSigned) / total) * 100 : 0,
        };
    }, [sendRequests]);

    // 만료 예정 동의서 계산 (Phase 4)
    const expiringConsents = useMemo(() => {
        const now = new Date();
        const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

        return sendRequests
            .filter(r => {
                // 아직 서명되지 않았고, 만료되지 않았으며, 만료 예정인 것
                if (r.status === 'signed' || r.status === 'expired') return false;
                if (!r.linkExpiresAt) return false;

                const expiresAt = new Date(r.linkExpiresAt);
                return expiresAt > now && expiresAt <= sevenDaysLater;
            })
            .map(r => {
                const expiresAt = new Date(r.linkExpiresAt!);
                const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
                return {
                    id: r.id,
                    customerName: r.customerName,
                    templateName: r.templateName,
                    expiresAt: r.linkExpiresAt!,
                    daysLeft,
                };
            })
            .sort((a, b) => a.daysLeft - b.daysLeft);
    }, [sendRequests]);

    // 만료 예정 동의서 재전송 핸들러 (Phase 4)
    const handleResendExpiring = useCallback((consentId: string) => {
        if (!groupId) return;

        resendRequestMutation.mutate({
            groupId,
            requestId: Number(consentId) as never,
        });
    }, [groupId, resendRequestMutation]);

    // PDF download handler for template
    const handleDownloadPdf = useCallback(() => {
        downloadTemplatePdf(templateFormData.name);
    }, [downloadTemplatePdf, templateFormData.name]);

    // PDF download handler for consent
    const handleDownloadConsentPdf = useCallback(() => {
        if (!selectedConsentForPreview) return;
        const fileName = `${selectedConsentForPreview.templateName}_${selectedConsentForPreview.customerName}_${selectedConsentForPreview.petName}.pdf`;
        downloadConsentPdf(fileName, selectedConsentForPreview.signatureImageUrl);
    }, [downloadConsentPdf, selectedConsentForPreview]);

    // Navigate to customer detail
    const handleNavigateToCustomer = useCallback((customerName: string, customerPhone: string) => {
        closeCustomerDetailModal();
        navigate(`/users?userId=${encodeURIComponent(customerPhone)}&userName=${encodeURIComponent(customerName)}&userPhone=${encodeURIComponent(customerPhone)}`);
    }, [navigate, closeCustomerDetailModal]);

    // Navigate to customer detail by ID (발송 이력에서 클릭 시)
    const handleNavigateToCustomerById = useCallback((customerId: string, templateId: number) => {
        // 고객 관리 페이지로 이동하면서 고객 ID와 동의서 탭 활성화, 서명 이력 탭, 템플릿 ID 전달
        navigate(`/users?customerId=${encodeURIComponent(customerId)}&tab=consent&consentLeftTab=history&templateId=${templateId}`);
    }, [navigate]);

    // 선택 정보 가져오기
    const getSelectedInfo = useCallback(() => {
        const customer = signatureCustomers.find((c) => c.id === signatureFormData.customerId);
        // selectedPetInfo를 우선 사용 (API에서 직접 가져온 데이터)
        const pet = selectedPetInfo || customer?.pets.find((p) => p.id === signatureFormData.petId);
        const template = templates.find((t) => String(t.id) === signatureFormData.templateId);
        return { customer, pet, template };
    }, [signatureCustomers, templates, signatureFormData, selectedPetInfo]);

    // ========== Send Consent Handlers ==========
    const handleOpenSendConsentModal = useCallback(() => {
        setIsSendConsentModalOpen(true);
    }, []);

    const handleCloseSendConsentModal = useCallback(() => {
        setIsSendConsentModalOpen(false);
    }, []);

    const handleSendConsent = useCallback((templateId: string, customerIds: string[], version: number, sendMethod: 'sms' | 'email' | 'kakao') => {
        if (!groupId) {
            toastMessage({ message: '그룹 정보가 없습니다.', type: 'error' });
            return;
        }

        // sendMethod를 API 형식으로 변환
        const apiSendMethod = sendMethod === 'sms' ? 'SMS' : sendMethod === 'email' ? 'EMAIL' : 'KAKAO';

        createBulkRequestsMutation.mutate({
            groupId,
            consentBulkConsentRequestCreateRequest: {
                templateId: Number(templateId),
                customerIds: customerIds,
                sendMethod: apiSendMethod,
                templateVersion: version,
            } as never,
        });

        handleCloseSendConsentModal();
    }, [groupId, createBulkRequestsMutation, handleCloseSendConsentModal]);

    // ========== Send History Handlers ==========
    const handleOpenSendHistoryModal = useCallback(() => {
        setIsSendHistoryModalOpen(true);
    }, []);

    const handleCloseSendHistoryModal = useCallback(() => {
        setIsSendHistoryModalOpen(false);
    }, []);

    const handleResendConsent = useCallback((requestId: string) => {
        if (!groupId) return;

        resendRequestMutation.mutate({
            groupId,
            requestId: Number(requestId) as never,
        });
    }, [groupId, resendRequestMutation]);

    const tabItems = [
        { value: 'profile', label: '매니저 정보', icon: User },
        { value: 'branch', label: '지점 정보', icon: Building2 },
        { value: 'operations', label: '운영 관리', icon: Settings2 },
        { value: 'consent', label: '동의서 관리', icon: FileTextIcon },
    ];

    const operationsSubTabItems = [
        { value: 'activity', label: '활동 타입 관리', icon: Activity },
        { value: 'activity-template', label: '활동 템플릿 관리', icon: LayoutTemplate },
        { value: 'pricing', label: '이용권 관리', icon: CreditCard },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">설정</h1>
                <p className="text-neutral-500 dark:text-neutral-400">계정 및 지점 정보를 관리하세요.</p>
            </div>

            {/* Error/Success Messages */}
            {error && (
                <div className="rounded-lg bg-danger-50 border border-danger-200 p-4 text-sm text-danger-600">
                    {error}
                </div>
            )}

            {(updateProfileMutation.isSuccess || updateBranchMutation.isSuccess) && (
                <div className="rounded-lg bg-success-50 border border-success-200 p-4 text-sm text-success-600">
                    정보가 성공적으로 업데이트되었습니다.
                </div>
            )}

            {/* Tabs */}
            <div className="border-b">
                <div className="flex gap-1">
                    {tabItems.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.value}
                                onClick={() => handleTabChange(tab.value as TabType)}
                                className={cn(
                                    'flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2',
                                    activeTab === tab.value
                                        ? 'border-orange-500 text-orange-600'
                                        : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200'
                                )}
                            >
                                <Icon className="h-4 w-4" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Profile Tab */}
            {activeTab === 'profile' && <ProfileTab />}

            {/* Branch Tab */}
            {activeTab === 'branch' && <BranchTab customerLength={customerListData?.totalElements || 0} />}

            {/* Operations Tab */}
            {activeTab === 'operations' && (
                <Card>
                    <CardContent className="p-6">
                        {/* 운영관리 서브탭 */}
                        <div className="flex gap-2 border-b pb-4 mb-6">
                            {operationsSubTabItems.map((subTab) => {
                                const Icon = subTab.icon;
                                return (
                                    <button
                                        key={subTab.value}
                                        onClick={() => handleOperationsSubTabChange(subTab.value as OperationsSubTab)}
                                        className={cn(
                                            'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                                            operationsSubTab === subTab.value
                                                ? 'bg-orange-100 text-orange-700'
                                                : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-700 dark:hover:text-neutral-200'
                                        )}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {subTab.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* 활동 타입 관리 서브탭 */}
                        {operationsSubTab === 'activity' && (
                            <ActivityTab />
                        )}

                        {/* 활동 템플릿 관리 서브탭 */}
                        {operationsSubTab === 'activity-template' && (
                            <ActivityTemplateTab />
                        )}

                        {/* 이용권 관리 서브탭 */}
                        {operationsSubTab === 'pricing' && (
                            <PricingPlanTab />
                        )}
                    </CardContent>
                </Card>
            )}

            {/* 동의서 관리 서브탭 */}
            {activeTab === 'consent' && (
                <ConsentTab
                    templates={templates}
                    onCreateTemplate={handleCreateTemplate}
                    onEditTemplate={handleEditTemplate}
                    onViewTemplate={handleViewTemplate}
                    onDeleteTemplate={handleDeleteConfirm}
                    onDuplicateTemplate={handleDuplicateTemplate}
                    onSignWithTemplate={openSignatureModalWithTemplate}
                    onSendConsent={handleOpenSendConsentModal}
                    onViewVersionHistory={handleOpenVersionHistory}
                    onSetActiveVersion={handleSetActiveVersion}
                    pageInfo={pageInfo}
                    onGoToPage={goToPage}
                    onGoToNextPage={goToNextPage}
                    onGoToPrevPage={goToPrevPage}
                    onGoToFirstPage={goToFirstPage}
                    onGoToLastPage={goToLastPage}
                />
            )}

            {/* ========== Modals ========== */}

            {/* Template Create/Edit Modal */}
            <TemplateModal
                isOpen={isTemplateModalOpen}
                onClose={() => setIsTemplateModalOpen(false)}
                mode={templateModalMode}
                formData={templateFormData}
                setFormData={setTemplateFormData}
                onSave={handleSaveTemplate}
                storeInfo={storeInfo}
                previewRef={previewRef}
                fileInputRef={fileInputRef}
                sensors={sensors}
                onFileUpload={handleFileUpload}
                onClearFileUpload={handleClearFileUpload}
                onDragEnd={handleDragEnd}
                onAddQuestion={handleAddQuestion}
                onDeleteQuestion={handleDeleteQuestion}
                onUpdateQuestion={handleUpdateQuestion}
                onQuestionTypeChange={handleQuestionTypeChange}
                onAddOption={handleAddOption}
                onDeleteOption={handleDeleteOption}
                onUpdateOption={handleUpdateOption}
                onDownloadPdf={handleDownloadPdf}
            />

            {/* Template View Modal */}
            <TemplateViewModal
                isOpen={isViewModalOpen}
                onClose={() => setIsViewModalOpen(false)}
                template={templateForView}
                onEdit={handleEditTemplate}
                storeInfo={storeInfo}
            />

            {/* Template Delete Modal */}
            <TemplateDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                template={selectedTemplate}
                onDelete={handleDeleteTemplate}
            />

            {/* Signature Request Modal */}
            <SignatureModal
                isOpen={isSignatureModalOpen}
                onClose={() => setIsSignatureModalOpen(false)}
                step={signatureStep}
                setStep={setSignatureStep}
                formData={signatureFormData}
                setFormData={setSignatureFormData}
                signatureDataUrl={signatureDataUrl}
                signatureCanvasRef={signatureCanvasRef}
                activeTemplates={activeTemplates}
                customers={signatureCustomers}
                templates={templates}
                getSelectedInfo={getSelectedInfo}
                onNextStep={goToNextStep}
                startDrawing={startDrawing}
                draw={draw}
                stopDrawing={stopDrawing}
                clearSignature={clearSignature}
                onComplete={handleCompleteSignature}
                groupId={groupId || 0}
                questionAnswers={questionAnswers}
                setQuestionAnswers={setQuestionAnswers}
                storeInfo={storeInfo}
            />

            {/* Signed Consent Detail Modal */}
            <SignedConsentDetailModal
                isOpen={isSignedDetailModalOpen}
                onClose={() => setIsSignedDetailModalOpen(false)}
                consent={selectedSignedConsent}
                templates={templates}
            />

            {/* Customer Detail Modal */}
            <CustomerDetailModal
                isOpen={isCustomerDetailModalOpen}
                onClose={() => setIsCustomerDetailModalOpen(false)}
                customerGroup={selectedCustomerGroup}
                selectedConsentForPreview={selectedConsentForPreview}
                onSelectConsent={handleSelectConsentForPreview}
                onNavigateToCustomer={handleNavigateToCustomer}
                onDownloadPdf={handleDownloadConsentPdf}
                storeInfo={storeInfo}
                templates={templates}
                previewConsentRef={previewConsentRef}
            />

            {/* Send Consent Modal */}
            <SendConsentModal
                isOpen={isSendConsentModalOpen}
                onClose={handleCloseSendConsentModal}
                templates={templates}
                mockCustomers={customers}
                onSend={handleSendConsent}
                groupId={groupId}
                onNavigateToCustomer={handleNavigateToCustomerById}
                storeInfo={storeInfo}
            />

            {/* Version History Modal */}
            <VersionHistoryModal
                isOpen={isVersionHistoryModalOpen}
                onClose={handleCloseVersionHistory}
                template={selectedTemplate}
                versionHistory={versionHistory}
                onSetActiveVersion={handleSetActiveVersion}
                storeInfo={storeInfo}
                groupId={groupId}
            />

            {/* Bulk Send Loading Modal */}
            <Dialog open={createBulkRequestsMutation.isPending}>
                <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                            동의서 발송 중
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-6">
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-full">
                                <Progress value={undefined} className="h-2 animate-pulse" />
                            </div>
                            <p className="text-sm text-neutral-500 dark:text-neutral-400">
                                동의서를 발송하고 있습니다. 잠시만 기다려주세요...
                            </p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Bulk Send Result Modal */}
            <Dialog open={isBulkSendResultModalOpen} onOpenChange={setIsBulkSendResultModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>동의서 발송 결과</DialogTitle>
                    </DialogHeader>
                    {bulkSendResult && (() => {
                        const successCount = bulkSendResult.successList.length;
                        const failureCount = bulkSendResult.failureList.length;
                        const totalCount = successCount + failureCount;
                        const successRate = totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0;

                        return (
                            <div className="py-4 space-y-4">
                                {/* Progress Bar */}
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-neutral-600 dark:text-neutral-300">발송 진행률</span>
                                        <span className="font-medium">{successRate}%</span>
                                    </div>
                                    <div className="relative h-3 w-full overflow-hidden rounded-full bg-neutral-200 dark:bg-neutral-700">
                                        <div
                                            className={cn(
                                                "h-full transition-all duration-500",
                                                failureCount === 0 ? "bg-green-500" : successCount === 0 ? "bg-red-500" : "bg-amber-500"
                                            )}
                                            style={{ width: `${successRate}%` }}
                                        />
                                    </div>
                                </div>

                                {/* Summary */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                                        <CheckCircle2 className="h-5 w-5 text-green-600" />
                                        <div>
                                            <p className="text-xs text-green-600">성공</p>
                                            <p className="text-lg font-semibold text-green-700">{successCount}명</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200">
                                        <XCircle className="h-5 w-5 text-red-600" />
                                        <div>
                                            <p className="text-xs text-red-600">실패</p>
                                            <p className="text-lg font-semibold text-red-700">{failureCount}명</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Failure Details */}
                                {failureCount > 0 && (
                                    <div className="space-y-2">
                                        <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">실패 상세</p>
                                        <div className="max-h-40 overflow-y-auto space-y-1">
                                            {bulkSendResult.failureList.map((item, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 bg-red-50 rounded text-sm">
                                                    <span className="text-neutral-700 dark:text-neutral-200">{item.customerName || item.userName || '알 수 없음'}</span>
                                                    <span className="text-red-600 text-xs">{item.reason || item.error || '발송 실패'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Success Message */}
                                {failureCount === 0 && successCount > 0 && (
                                    <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                                        <p className="text-green-700 font-medium">모든 동의서가 성공적으로 발송되었습니다!</p>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                    <DialogFooter>
                        <Button onClick={() => setIsBulkSendResultModalOpen(false)}>
                            확인
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
    );
}
